import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { Snackbar } from '@/components/Snackbar';
import { useToastSlot } from '@/components/toastStack';
import { dueLabel, reminderLabel } from '@/features/tasks/due';
import { todayCalendarDay } from '@/utils/format';
import type { EmailTasksSaved } from './AddToTaskDialog';
import { deleteEmailTasks } from './deleteEmailTasks';

/**
 * "Added to My Tasks · Tomorrow, 9:00 AM · Undo" (ADR 0075): the mail view's
 * word that a task was made from an email, with Undo, which deletes it again.
 *
 * Its own region in the shell's toast stack (toastStack.ts), beside the mail
 * view's Undo: an archive's Undo and this one can both be on screen, and each
 * must undo its own thing.
 */

const NOTICE_MS = 8000;

interface Notice {
  id: number;
  text: string;
  undo?: () => void;
}

/** The sentence for a saved batch. */
export function emailTasksSavedText(saved: EmailTasksSaved, today: string = todayCalendarDay()): string {
  const count = saved.created.length;
  const where = saved.listName ?? 'Tasks';
  const parts = [count === 1 ? `Added to ${where}` : `${count} tasks added to ${where}`];
  const first = saved.created[0]?.task;
  if (first?.remindAt) parts.push(`reminder ${reminderLabel(first.remindAt, today).text}`);
  else if (saved.dueDate !== null) parts.push(`due ${dueLabel(saved.dueDate, saved.dueTime, today).text}`);
  if (saved.failed.length > 0) parts.push(`${saved.failed.length} couldn’t be added`);
  return parts.join(' · ');
}

export function useEmailTaskSnackbar(): { announce: (saved: EmailTasksSaved) => void; snackbar: ReactNode } {
  const api = useApi();
  const client = useQueryClient();
  const slot = useToastSlot();
  const [notice, setNotice] = useState<Notice | undefined>();
  const [held, setHeld] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!notice || held) return;
    const timer = setTimeout(() => setNotice(undefined), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice, held]);

  const announce = useCallback(
    (saved: EmailTasksSaved) => {
      seq.current += 1;
      const id = seq.current;
      setNotice({
        id,
        text: emailTasksSavedText(saved),
        undo: () => {
          void deleteEmailTasks(api, client, saved.created).then(
            () => {
              seq.current += 1;
              setNotice({
                id: seq.current,
                text: saved.created.length === 1 ? 'Task removed' : `${saved.created.length} tasks removed`,
              });
            },
            () => {
              seq.current += 1;
              setNotice({ id: seq.current, text: 'The task could not be removed. Delete it from Tasks.' });
            },
          );
        },
      });
    },
    [api, client],
  );

  const snackbar = (
    <Snackbar
      className="snackbar--email-task"
      message={notice?.text}
      {...(notice ? { messageKey: `email-task-${notice.id}` } : {})}
      {...(notice?.undo
        ? {
            actionLabel: 'Undo',
            onAction: () => {
              const undo = notice.undo;
              setNotice(undefined);
              undo?.();
            },
          }
        : {})}
      onDismiss={() => setNotice(undefined)}
      onHoldChange={setHeld}
    />
  );
  return { announce, snackbar: slot ? createPortal(snackbar, slot) : snackbar };
}
